import { computed } from 'vue';

export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  DIRECTEUR: 'DIRECTEUR',
  CHEF_BUREAU: 'CHEF_BUREAU',
  VETERINAIRE: 'VETERINAIRE',
  ETALONNIER: 'ETALONNIER',
  NAISSEUR: 'NAISSEUR',
  PROPRIETAIRE: 'PROPRIETAIRE',
  UTILISATEUR: 'UTILISATEUR',
  REPRESENTANT_HARAS_REGIONAL: 'REPRESENTANT_HARAS_REGIONAL',
  RESPONSABLE_HARAS: 'RESPONSABLE_HARAS',
} as const;

export function usePermissions() {
  const user = computed(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const userRoles = computed(() => {
    try {
      if (!user.value?.roles) return [];
      return user.value.roles.map((r: any) => r.code || r);
    } catch {
      return [];
    }
  });

  /**
   * Vérifie si l'utilisateur a UN DES rôles spécifiés
   */
  const hasRole = (roleOrRoles: string | string[]): boolean => {
    try {
      if (!user.value) return false;
      const rolesToCheck = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
      return rolesToCheck.some(role => userRoles.value.includes(role));
    } catch {
      return false;
    }
  };

  /**
   * Vérifie si l'utilisateur peut effectuer une action spécifique
   */
  const canDo = (action: string): boolean => {
    try {
    const permissions: Record<string, string[]> = {
      // Identification Terrain
      'configurer_haras': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.DIRECTEUR],
      'completer_naisseur': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.DIRECTEUR],
      'creer_signalement': [ROLES.VETERINAIRE, ROLES.SUPER_ADMIN, ROLES.ADMIN],
      'valider_signalement': [ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],

      // Chevaux
      'creer_cheval': [ROLES.CHEF_BUREAU, ROLES.ETALONNIER, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'modifier_cheval': [ROLES.CHEF_BUREAU, ROLES.ETALONNIER, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'supprimer_cheval': [ROLES.SUPER_ADMIN, ROLES.ADMIN],

      // Déclarations
      'declarer_naissance': [ROLES.NAISSEUR, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN, ROLES.REPRESENTANT_HARAS_REGIONAL, ROLES.RESPONSABLE_HARAS],
      'signer_naissance_naisseur': [ROLES.NAISSEUR, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN, ROLES.REPRESENTANT_HARAS_REGIONAL, ROLES.RESPONSABLE_HARAS],
      'signer_naissance_veterinaire': [ROLES.VETERINAIRE, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'soumettre_declaration': [ROLES.NAISSEUR, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN, ROLES.REPRESENTANT_HARAS_REGIONAL, ROLES.RESPONSABLE_HARAS],
      'valider_declaration': [ROLES.ADMIN, ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'rejeter_declaration': [ROLES.ADMIN, ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],

      // Saillies
      'creer_saillie': [ROLES.ETALONNIER, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'valider_saillie': [ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],

      // Qualification
      'creer_session': [ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'inscrire_qualification': [ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
      'examiner_cheval': [ROLES.VETERINAIRE, ROLES.SUPER_ADMIN],
      'valider_qualification': [ROLES.CHEF_BUREAU, ROLES.DIRECTEUR, ROLES.SUPER_ADMIN],
    };

      const allowedRoles = permissions[action];
      if (!allowedRoles) return false;

      return allowedRoles.some(role => userRoles.value.includes(role));
    } catch {
      return false;
    }
  };

  return {
    user,
    userRoles,
    hasRole,
    canDo,
    ROLES,
  };
}
